import { useFormik } from 'formik';
import ProjectCreateImport from 'app/components/ProjectCreateImport';
import {
	Accordion as MuiAccordion,
	AccordionDetails,
	AccordionProps,
	AccordionSummary as MuiAccordionSummary,
	accordionSummaryClasses,
	AccordionSummaryProps,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	styled,
	Typography,
} from '@mui/material';
import { FormTextField } from 'shared/_ui/form/form-text-field';
import ArrowForwardIosSharp from '@mui/icons-material/ArrowForwardIosSharp';
import { AddFileToNewProjectButton } from 'app/_model/project/components/AddFileButton';
import SwitchField from 'shared/_ui/form/switch-field';
import {
	IconCloud,
	IconCloudWithCheck,
	IconLocalStorage,
	IconLocalStorageWithCheck,
} from 'shared/_ui/icon';
import { PROJECT_SCHEMA } from 'shared/constants/general';
import { formatDateApi } from 'shared/utils/format';
import useProjectCreate from './use-project-create';
import ProjectCreateFiles from './project-create-files';

type ProjectCreateProps = {
	onClose: () => void;
};

const Accordion = styled(
	(props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />,
	{ name: 'Accordion' }
)(() => ({
	border: 'none',
	'.MuiAccordionSummary-root': {
		paddingInline: '0',
	},
	'&:not(:last-child)': {
		borderBottom: 0,
	},
	'&::before': {
		display: 'none',
	},
}));

const AccordionSummary = styled(
	(props: AccordionSummaryProps) => (
		<MuiAccordionSummary
			expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
			{...props}
		/>
	),
	{ name: 'AccordionSummary' }
)(({ theme }) => ({
	flexDirection: 'row-reverse',
	[`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
		transform: 'rotate(90deg)',
	},
	[`& .${accordionSummaryClasses.content}`]: {
		marginLeft: theme.spacing(1),
		color: theme.palette.text.secondary,
	},
}));

export default function ProjectCreate({ onClose }: ProjectCreateProps) {
	const {
		canUseCloud,
		showCreatedDate,
		project,
		files,
		handleSubmit,
		handleAddFiles,
		handleSelectFile,
		handleImportFiles,
		handleDeleteFiles,
		chosenType,
		setChosenType,
		showFiles,
	} = useProjectCreate();

	const formik = useFormik({
		initialValues: project,
		onSubmit: handleSubmit,
		validationSchema: PROJECT_SCHEMA,
	});

	const { isSubmitting, values, setFieldValue } = formik;

	return (
		<Dialog open fullWidth maxWidth="sm">
			<DialogTitle variant="h2">Okay, lets create a new&nbsp;project</DialogTitle>
			<DialogContent>
				<form onSubmit={formik.handleSubmit}>
					<FormTextField
						name="title"
						formik={formik}
						fullWidth
						label="Title:"
						labelProps={{ position: 'top' }}
						placeholder="Give your project a title"
						spellCheck
					/>
					<FormTextField
						name="description"
						formik={formik}
						fullWidth
						multiline
						minRows={2}
						label="Project Notes:"
						labelProps={{ position: 'top' }}
						placeholder="Brief description about your project"
						spellCheck
					/>
					{showCreatedDate && (
						<FormTextField
							name="created"
							formik={formik}
							fullWidth
							label="Created:"
							type="date"
							labelProps={{ position: 'top' }}
						/>
					)}
					<Box
						sx={{
							width: '100%',
							display: 'flex',
							columnGap: 2,
							justifyContent: 'space-between',
							flexWrap: 'wrap',
						}}
					>
						<SwitchField
							name="storeOnLocalDrive"
							formik={formik}
							label={
								<Box
									component="span"
									sx={{
										whiteSpace: 'nowrap',
									}}
								>
									Store on local drive:
									{!values.storeOnLocalDrive && (
										<IconLocalStorage
											color="secondary"
											sx={{ marginLeft: (theme) => theme.spacing(2) }}
										/>
									)}
									{values.storeOnLocalDrive && (
										<IconLocalStorageWithCheck
											color="success"
											sx={{ marginLeft: (theme) => theme.spacing(2) }}
										/>
									)}
								</Box>
							}
							onChange={(e, checked) => {
								if (canUseCloud) {
									formik.setFieldValue('storeOnLocalDrive', checked);
									if (!checked && !values.storeInCloud) {
										formik.setFieldValue('storeInCloud', true);
									}
								} else {
									formik.setFieldValue('storeInCloud', true);
								}
								formik.setFieldValue('storeOnLocalDrive', checked);
							}}
						/>
						{canUseCloud && (
							<SwitchField
								name="storeInCloud"
								formik={formik}
								label={
									<Box
										component="span"
										sx={{
											whiteSpace: 'nowrap',
										}}
									>
										Store in cloud:
										{!values.storeInCloud && (
											<IconCloud
												color="secondary"
												sx={{ marginLeft: (theme) => theme.spacing(2) }}
											/>
										)}
										{values.storeInCloud && (
											<IconCloudWithCheck
												color="success"
												sx={{ marginLeft: (theme) => theme.spacing(2) }}
											/>
										)}
									</Box>
								}
								onChange={(e, checked) => {
									formik.setFieldValue('storeInCloud', checked);
									if (!checked && !values.storeOnLocalDrive) {
										formik.setFieldValue('storeOnLocalDrive', true);
									}
								}}
							/>
						)}
					</Box>
					<Accordion>
						<AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
							<Typography component="span">Advanced</Typography>
						</AccordionSummary>
						<AccordionDetails
							sx={{ padding: 0, display: 'grid', gridTemplateColumns: '1fr auto', gap: 2 }}
						>
							<Typography
								component="span"
								sx={{
									color: 'label.main',
								}}
							>
								{files.length
									? `You have already added ${files.length} files`
									: 'Add files to your project'}
							</Typography>
							<AddFileToNewProjectButton
								type={chosenType}
								addFiles={handleAddFiles}
								disabled={isSubmitting}
								buttonProps={{ size: 'small', sx: { width: '100%' } }}
							/>
							<ProjectCreateImport
								setMinimumDate={(date) => date && setFieldValue('created', formatDateApi(date))}
								addFiles={handleImportFiles}
							/>
						</AccordionDetails>
					</Accordion>
					{showFiles && (
						<Accordion defaultExpanded>
							<AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
								<Typography component="span">Files ({files.length})</Typography>
							</AccordionSummary>
							<AccordionDetails
								sx={{ display: 'flex', flexDirection: 'column', rowGap: 3, padding: 0 }}
							>
								<ProjectCreateFiles
									defaultType={chosenType}
									setDefaultType={setChosenType}
									files={files}
									handleAddFiles={handleAddFiles}
									handleSelectFile={handleSelectFile}
									handleDeleteFiles={handleDeleteFiles}
									disabled={isSubmitting}
									showFiles={showFiles}
								/>
							</AccordionDetails>
						</Accordion>
					)}
				</form>
			</DialogContent>
			<DialogActions>
				<Button color="secondary" onClick={onClose} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button type="submit" disabled={isSubmitting} onClick={() => formik.handleSubmit()}>
					Create
				</Button>
			</DialogActions>
		</Dialog>
	);
}
